import { 
  FaLinkedin, 
  FaFacebook, 
  FaTwitter, 
  FaTelegram, 
  FaWhatsapp, 
  FaInstagram,
  FaCopy,
  FaShareAlt
} from 'react-icons/fa';
import './ShareJobButtons.css';

function ShareJobButtons({ jobTitle, company, jobUrl, job, className = "" }) {
  const currentUrl = window.location.href;
  
  // Get job image - prioritize company logo, then job image, then fallback
  const getJobImage = () => {
    return job?.hiringOrganization?.logo || 
           job?.image || 
           job?.companyLogo || 
           job?.logo || 
           'https://jobsopportunity.in/images/job-placeholder.jpg';
  };
  
  // Enhanced share text with comprehensive job details and image
  const getShareText = () => {
    let text = `🚀 Amazing Job Opportunity!\n\n`;
    text += `📋 Position: ${jobTitle}\n`;
    
    if (company) {
      text += `🏢 Company: ${company}\n`;
    }
    
    if (job?.location) {
      const location = typeof job.location === 'string' 
        ? job.location 
        : job.location?.address || job.location?.city || 'Location not specified';
      text += `📍 Location: ${location}\n`;
    }
    
    if (job?.salary) {
      const { min, max, currency, period } = job.salary;
      const currencySymbol = currency === 'USD' ? '$' : currency;
      const periodText = period === 'yearly' ? 'year' : period === 'monthly' ? 'month' : 'hour';
      
      if (min && max) {
        text += `💰 Salary: ${currencySymbol}${min.toLocaleString()} - ${currencySymbol}${max.toLocaleString()} per ${periodText}\n`;
      } else if (min) {
        text += `💰 Salary: ${currencySymbol}${min.toLocaleString()} per ${periodText}\n`;
      } else if (max) {
        text += `💰 Salary: Up to ${currencySymbol}${max.toLocaleString()} per ${periodText}\n`;
      }
    }
    
    if (job?.jobType) {
      text += `⏰ Type: ${job.jobType.replace('_', ' ').toUpperCase()}\n`;
    }
    
    if (job?.description) {
      // Clean HTML and get first 100 characters
      const cleanDesc = job.description.replace(/<[^>]*>/g, '').substring(0, 100);
      text += `📝 ${cleanDesc}...\n`;
    }
    
    text += `\n🖼️ Job Image: ${getJobImage()}\n`;
    text += `🔗 Apply now: ${currentUrl}\n`;
    text += `\n#JobOpportunity #Hiring #Career`;
    
    return text;
  };

  const shareText = getShareText();
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(currentUrl);
  const jobImage = getJobImage();

  // Enhanced share links with professional icons
  const shareLinks = [
    {
      name: 'LinkedIn',
      icon: <FaLinkedin className="share-icon-svg" />,
      getUrl: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodeURIComponent(jobTitle)}&summary=${encodeURIComponent(shareText)}&source=${encodeURIComponent('JobsOpportunity.in')}`
    },
    {
      name: 'Facebook',
      icon: <FaFacebook className="share-icon-svg" />,
      getUrl: () => `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(shareText)}&picture=${encodeURIComponent(jobImage)}`
    },
    {
      name: 'Twitter',
      icon: <FaTwitter className="share-icon-svg" />,
      getUrl: () => `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
    },
    {
      name: 'WhatsApp',
      icon: <FaWhatsapp className="share-icon-svg" />,
      getUrl: () => `https://wa.me/?text=${encodedText}`
    },
    {
      name: 'Telegram',
      icon: <FaTelegram className="share-icon-svg" />,
      getUrl: () => `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`
    },
    {
      name: 'Instagram',
      icon: <FaInstagram className="share-icon-svg" />,
      getUrl: () => {
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
          return `instagram://app`;
        }
        return `https://www.instagram.com/`;
      },
      onClick: () => {
        // Enhanced Instagram sharing with comprehensive details and image
        const instagramText = `🚀 Job Opportunity Alert!\n\n${shareText}`;
        navigator.clipboard.writeText(instagramText)
          .then(() => {
            alert('Job details with image URL copied! Open Instagram and paste to share with all the details and image.');
          })
          .catch(err => {
            console.error('Failed to copy:', err);
          });
      }
    }
  ];

  const handleShare = (link) => {
    if (link.name === 'Instagram') {
      link.onClick();
      const width = 600;
      const height = 400;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      window.open(link.getUrl(), 'Share', `width=${width},height=${height},left=${left},top=${top}`);
    } else {
      const width = 600;
      const height = 400;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      window.open(link.getUrl(), 'Share', `width=${width},height=${height},left=${left},top=${top}`);
    }
  };

  // Copy to clipboard function with image
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareText)
      .then(() => {
        alert('Job details with image URL copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  return (
    <div className={`share-job-buttons ${className}`}>
      <h3>
        <FaShareAlt className="section-icon" />
        Share this job
      </h3>
      <div className="share-buttons-grid">
        {shareLinks.map(link => (
          <button 
            key={link.name}
            onClick={() => handleShare(link)}
            className={`share-button ${link.name.toLowerCase().replace(/\s+/g, '-')}`}
            aria-label={`Share on ${link.name}`}
          >
            {link.icon}
            <span>Share on {link.name}</span>
          </button>
        ))}
        <button 
          onClick={copyToClipboard}
          className="share-button copy-link"
          aria-label="Copy job details"
        >
          <FaCopy className="share-icon-svg" />
          <span>Copy Details</span>
        </button>
      </div>
    </div>
  );
}

export default ShareJobButtons;